import {Box, DSButton, DSIconButton, DSIconJourney, Row} from "@cdx/ds";
import {History} from "history";
import CardIdButton from "./CardIdButton";
import {DueDateCardHintIcon as RawDueDateCardHintIcon} from "../due-date/DueDateCardHintIcon";
import {
  ThemedMilestoneIcon as RawThemedMilestoneIcon,
  getMilestoneState,
  setMsThemeColor,
} from "../milestones/milestone-utils";
import {
  DefaultOverlay,
  SpawnAnchoredOverlayWithNode,
  TooltipForChild,
  getMetaKeyLabel,
  isWithMetaKey,
  useGlobalKeyPress,
} from "@cdx/common";
import routes from "../../routes";
import {MilestoneTooltipForChild as RawMilestoneTooltipForChild} from "../../pages/milestones/MilestoneTooltip";
import {getDueDateInfo} from "../due-date/due-date-utils";
import {TrackingTooltip, trackedTimeToProgress} from "../../components/Card/CardBottomHints";
import {getTotalTimeForCard} from "../time-tracking/time-tracking-utils";
import {Card, CardId} from "../../cdx-models/Card";
import {Root} from "../../cdx-models/Root";
import {Milestone} from "../../cdx-models/Milestone";
import {ArenaCtx} from "../card-panel/card-panel-utils";
import {Sprint, SprintId} from "../../cdx-models/Sprint";
import {ThemedSprintIconWithTooltipAndMetaClick, getSprintState} from "../milestones/sprint-utils";
import {CardOrder} from "../card-order/useCardOrder";
import {forwardRef, useRef, useState} from "react";
import {ZoneOverlay} from "../../components/Card/useHoverShortcuts";
import {SharedPropChangeOverlayForChild} from "../../components/PropEdit/shared";
import {useWorkflowApplicationOverlay} from "../workflows/WorkflowApplicationOverlay";
import {StopWatchIconWithProgress} from "./AnimatedStopWatchIcon";

const MilestoneTooltipForChild = RawMilestoneTooltipForChild as any;
const ThemedMilestoneIcon = RawThemedMilestoneIcon as any;
const DueDateCardHintIcon = RawDueDateCardHintIcon as any;

type MilestoneHintProps = {
  card: Card;
  root: Root;
  onClick?: (e: any) => void;
  milestone: Milestone;
  arenaCtx: ArenaCtx;
  history: History;
  canModify: boolean | null;
  onCardChange?: (arg: any) => unknown;
};
const MilestoneHint = ({
  card,
  root,
  onClick,
  milestone,
  arenaCtx,
  history,
  canModify,
  onCardChange,
}: MilestoneHintProps) => {
  const {accountSeq, title} = card;
  const milestoneState = milestone && getMilestoneState(root.account, milestone);

  const msSeq = milestone.$meta.get("accountSeq", null);
  const handleClick = (e: any) => {
    if (isWithMetaKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      if (msSeq) history.push(routes.milestoneWithCard.getUrl(msSeq, {accountSeq, title}));
    } else {
      onClick?.(e);
    }
  };

  const content = (
    <MilestoneTooltipForChild
      milestone={milestone}
      tooltip={
        arenaCtx.type !== "milestone" &&
        process.env.REACT_APP_MODE !== "open" &&
        `${getMetaKeyLabel()} + click to open card within milestone view`
      }
    >
      <ThemedMilestoneIcon
        theme={setMsThemeColor(milestone)}
        size={16}
        milestoneState={milestoneState}
        onClick={process.env.REACT_APP_MODE !== "open" && handleClick}
      />
    </MilestoneTooltipForChild>
  );

  return canModify ? (
    <SharedPropChangeOverlayForChild
      card={card}
      onChange={onCardChange}
      availableShortcuts={["milestone"]}
      cardContainerKey="openCard-milestone"
      getChildProps={(open) => ({hidden: open})}
      onClick={handleClick}
    >
      {content}
    </SharedPropChangeOverlayForChild>
  ) : (
    content
  );
};

type SprintHintProps = {
  card: Card;
  sprint: Sprint;
  canModify: boolean | null;
};
const SprintHint = ({card, sprint, canModify}: SprintHintProps) => (
  <ThemedSprintIconWithTooltipAndMetaClick
    theme={setMsThemeColor(sprint.sprintConfig)}
    cardId={card.cardId as CardId}
    sprintId={sprint.id as SprintId}
    sprintState={getSprintState(sprint)}
    propChangeInfo={
      canModify && {
        cardId: card.cardId as CardId,
        cardContainerKey: "openCard-sprint",
        availableShortcuts: ["sprint"],
      }
    }
  />
);

const TrackingHint = ({root, card}: {card: Card; root: Root}) => {
  const totalTrackingTime = root.account.timeTrackingMode !== "none" && getTotalTimeForCard(card);
  if (!totalTrackingTime) return null;
  return (
    <TooltipForChild tooltip={<TrackingTooltip cardId={card.cardId} />}>
      <StopWatchIconWithProgress size={20} progress={trackedTimeToProgress(totalTrackingTime)} />
    </TooltipForChild>
  );
};
type StatusIconsProps = {
  card: Card;
  root: Root;
  arenaCtx: any;
  history: History;
  canModify: boolean | null;
  onCardChange?: (arg: any) => unknown;
};
const StatusIcons = forwardRef<HTMLDivElement, StatusIconsProps>(
  ({card, root, arenaCtx, history, canModify, onCardChange}, ref) => {
    const dueDateInfo =
      process.env.REACT_APP_MODE !== "open" && getDueDateInfo({card, account: root.account});
    const sprint = process.env.REACT_APP_MODE !== "open" && card.$meta.get("sprint", null);
    const milestone = process.env.REACT_APP_MODE !== "open" && card.$meta.get("milestone", null);
    return (
      <Row relative ref={ref}>
        <Box
          absolute
          left="0"
          bottom="0"
          style={{
            top: "-32px",
            right: "-160px",
            background: `linear-gradient(-166deg, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 0.95) 80%)`,
          }}
        />
        <Row relative sp="8px" pointerEvents="auto" align="center" pl="16px" pr="4px" pb="8px">
          <CardIdButton card={card} />
          {dueDateInfo && <DueDateCardHintIcon {...dueDateInfo} />}
          {sprint && <SprintHint sprint={sprint} card={card} canModify={canModify} />}
          {milestone && (
            <MilestoneHint
              root={root}
              milestone={milestone}
              card={card}
              arenaCtx={arenaCtx}
              history={history}
              canModify={canModify}
              onCardChange={onCardChange}
            />
          )}
          {process.env.REACT_APP_MODE !== "open" && <TrackingHint root={root} card={card} />}
        </Row>
      </Row>
    );
  }
);

const ApplyWorkflowButton = ({card, root}: {card: Card; root: Root}) => {
  const {modal, setModalOpen} = useWorkflowApplicationOverlay({root, card});

  return (
    <>
      {modal}
      <DSIconButton
        onClick={() => setModalOpen(true)}
        size="md"
        variant="secondary"
        icon={<DSIconJourney />}
        label="Journey"
      />
    </>
  );
};

type OpenCardStatusRowProps = {
  card: Card;
  root: Root;
  arenaCtx: ArenaCtx;
  history: History;
  canModify: boolean | null;
  onOpenEditPanel: (e: any) => void;
  cardOrder?: CardOrder | null;
  onCardChange?: (arg: any) => unknown;
};
export const OpenCardStatusRow = (props: OpenCardStatusRowProps) => {
  const {card, root, arenaCtx, history, canModify, onOpenEditPanel, cardOrder, onCardChange} =
    props;
  const showStartJourney = () => {
    if (!card.deck) return false;
    if (card.parentCard || card.isDoc || card.visibility !== "default") return false;
    const withSteps = card.deck.$meta.exists("workflowItems", {visibility: "default"});
    if (!withSteps) return false;
    return true;
  };

  useGlobalKeyPress({
    key: "Enter",
    disabled: !canModify,
    fn: onOpenEditPanel,
    category: "Card detail view",
    description: "Edit card contents",
  });

  const zonesActive = cardOrder?.prop === "manual" && Boolean(arenaCtx.model?.manualOrderLabels);

  const [showZoneOverlay, setShowZoneOverlay] = useState(false);

  useGlobalKeyPress({
    code: "KeyD",
    withShift: true,
    disabled: process.env.REACT_APP_MODE === "open" || !zonesActive,
    fn: () => setShowZoneOverlay((v) => !v),
  });

  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <Row
      absolute
      sp="4px"
      bottom="0"
      left="0"
      pointerEvents="none"
      align="end"
      style={{
        right: 8 /* for the scrollbar */,
        background: `linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.95) 80%)`,
      }}
    >
      <SpawnAnchoredOverlayWithNode
        node={nodeRef.current}
        isOpen={showZoneOverlay}
        placement="right"
        renderOverlay={(overlayProps: any) => (
          <DefaultOverlay {...overlayProps}>
            <ZoneOverlay
              cardId={card.cardId as CardId}
              onClose={() => setShowZoneOverlay(false)}
              arenaCtx={arenaCtx}
            />
          </DefaultOverlay>
        )}
      >
        <StatusIcons
          card={card}
          root={root}
          arenaCtx={arenaCtx}
          history={history}
          ref={nodeRef}
          canModify={canModify}
          onCardChange={onCardChange}
        />
      </SpawnAnchoredOverlayWithNode>
      {canModify && (
        <Row pointerEvents="auto" sp="8px" align="center" ml="auto" pb="8px" pr="8px">
          {showStartJourney() && <ApplyWorkflowButton card={card} root={root} />}
          <DSButton onClick={onOpenEditPanel} size="md">
            Edit
          </DSButton>
        </Row>
      )}
    </Row>
  );
};
